import { DatePipe, NgStyle, registerLocaleData } from '@angular/common';
import localeDe from '@angular/common/locales/de';
import localeEs from '@angular/common/locales/es';
import localeNl from '@angular/common/locales/nl';
import { Component, DestroyRef, OnInit, effect, inject } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import {
  faArrowRight,
  faArrowsRotate,
  faCheck,
  faCircleNotch,
  faTimes,
  faXmark,
} from '@fortawesome/pro-solid-svg-icons';
import { LangChangeEvent, TranslatePipe, TranslateService } from '@ngx-translate/core';
import Keycloak from 'keycloak-js';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ButtonDirective } from 'primeng/button';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { PrimeNG } from 'primeng/config';
import { Toast } from 'primeng/toast';
import { filter } from 'rxjs';
import { TaskService } from './layout/topbar/components/tasks-sidebar/services/task.service';
import { WikiService } from './rgs/components/admin/components/wiki/services/wiki.service';
import { BuildInfoService } from './services/build-info/build-info.service';
import { EnvironmentService } from './services/environment/environment.service';
import { RgsKeycloakProfile } from './shared/models/rgs-keycloak-profile.model';
import { PermissionsService } from './shared/services/permissions.service';
import { ToastUtils } from './shared/utils/toast.utils';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  imports: [RouterOutlet, Toast, ConfirmDialog, FaIconComponent, ButtonDirective, NgStyle, DatePipe, TranslatePipe],
})
export class AppComponent implements OnInit {
  public readonly translateService = inject(TranslateService);
  private readonly keycloak = inject(Keycloak);
  private readonly router = inject(Router);
  private readonly environmentService = inject(EnvironmentService);
  private readonly permissionsService = inject(PermissionsService);
  private readonly buildInfoService = inject(BuildInfoService);
  private readonly messageService = inject(MessageService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly taskService = inject(TaskService);
  private readonly primeng = inject(PrimeNG);
  private readonly wikiService = inject(WikiService);
  private readonly destroyRef = inject(DestroyRef);

  public faTimes = faTimes;
  public faArrowsRotate = faArrowsRotate;
  public faXmark = faXmark;
  public faCheck = faCheck;
  public faCircleNotch = faCircleNotch;
  public faArrowRight = faArrowRight;

  /** PrimeNG only renders confirm icons via templates when the dialog has no icon inputs, so read them from the active confirmation. */
  public readonly confirmation = toSignal(this.confirmationService.requireConfirmation$);

  constructor() {
    effect(() => {
      const isLoading = this.buildInfoService.buildInfoResource.isLoading();
      const hasValue = this.buildInfoService.buildInfoResource.hasValue();

      if (isLoading || !hasValue) {
        return;
      }

      const buildInfo = this.buildInfoService.buildInfoResource.value();

      if (buildInfo) {
        const storedBuildInfoVersion = localStorage.getItem('buildInfoVersion');
        const storedBuildInfoCommitHash = localStorage.getItem('buildInfoCommitHash');
        const storedBuildInfoTimestamp =
          localStorage.getItem('buildInfoTimestamp') !== null
            ? new Date(localStorage.getItem('buildInfoTimestamp')!)
            : null;

        const buildInfoVersion = buildInfo.version;
        const buildInfoCommitHash = buildInfo.commitHash;
        const buildInfoTimestamp = buildInfo.timestamp ? new Date(buildInfo.timestamp) : null;

        if (buildInfoVersion === undefined || buildInfoCommitHash === undefined || buildInfoTimestamp === null) {
          console.warn('Build info is not defined');
          return;
        }

        if (
          storedBuildInfoVersion !== buildInfoVersion ||
          storedBuildInfoCommitHash !== buildInfoCommitHash ||
          storedBuildInfoTimestamp?.getTime() !== buildInfoTimestamp.getTime()
        ) {
          localStorage.setItem('buildInfoVersion', buildInfoVersion);
          localStorage.setItem('buildInfoCommitHash', buildInfoCommitHash);
          localStorage.setItem('buildInfoTimestamp', buildInfoTimestamp.toISOString());
          this.reload();
        }
      }
    });
  }

  public ngOnInit(): void {
    this.initLanguageConfig();
    this.initKeycloakConfig();
    this.unloadLicenseIfUserIsAdmin();
    this.redirectToKibIfKibUser();
    this.loadWikiPages();
    this.addRouteDetector();
  }

  public initLanguageConfig() {
    const supportedLanguages = ['en', 'nl', 'de', 'es'];
    const defaultLang = 'nl';

    this.translateService.addLangs(supportedLanguages);
    this.translateService.setFallbackLang(defaultLang);

    this.translateService.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event: LangChangeEvent) => this.updateHtmlLang(event.lang));

    const savedLocale = localStorage.getItem('locale');
    let selectedLang: string;

    if (savedLocale && supportedLanguages.includes(savedLocale)) {
      selectedLang = savedLocale;
    } else {
      const browserLang = this.translateService.getBrowserLang();
      const browserSupported = browserLang && supportedLanguages.includes(browserLang);
      selectedLang = browserSupported ? browserLang : defaultLang;
    }

    this.translateService.use(selectedLang);
    this.updateHtmlLang(selectedLang);

    registerLocaleData(localeNl, 'nl');
    registerLocaleData(localeDe, 'de');
    registerLocaleData(localeEs, 'es');

    this.translateService.get('primeng').subscribe((res) => this.primeng.setTranslation(res));
  }

  public initKeycloakConfig() {
    const route = window.location.href;

    if (route.includes('downloaded')) {
      return;
    }

    const sessionEnvironment = window.sessionStorage.getItem('environment');
    if (!this.keycloak.authenticated) {
      this.keycloak.login();
    }

    this.keycloak.loadUserProfile().then((profile) => {
      const keycloakProfile: RgsKeycloakProfile = profile as RgsKeycloakProfile;
      this.permissionsService.userProfile.set(keycloakProfile);

      const tenantClaim = (this.keycloak.tokenParsed as unknown as { tenant?: string } | undefined)?.tenant;
      const tenant = tenantClaim ?? 'nl';
      window.sessionStorage.setItem('tenant', tenant);
      this.permissionsService.tenant$.next(tenant);

      // Tenant is known: load the user from the correct database.
      if (keycloakProfile?.id) {
        this.permissionsService.loadUser(keycloakProfile.id);
      }

      if (keycloakProfile?.attributes?.licentie?.length > 1 && !sessionEnvironment) {
        if (route) {
          this.environmentService.oldUrl = route;
        }

        this.router.navigateByUrl('environment-selector');
      }

      if (keycloakProfile?.attributes?.licentie?.length === 1 && !sessionEnvironment) {
        const license = keycloakProfile?.attributes?.licentie[0];
        window.sessionStorage.setItem('environment', license);
        this.permissionsService.license$.next(license);
      }
    });
  }

  public unloadLicenseIfUserIsAdmin() {
    // const roles = this.keycloakService.getUserRoles();
    // if (roles.includes('admin')) {
    //   this.restService.post('/licensecontroller', { company: null }, 'text').pipe(take(1)).subscribe();
    // }
  }

  public redirectToKibIfKibUser() {
    // const roles = this.keycloakService.getUserRoles();
    // const kibRoles = ['kib_admin', 'kib_user'];
    // const keycloakProfile: RgsKeycloakProfile = this.keycloakService.getKeycloakInstance()
    //   .profile as RgsKeycloakProfile;
    // const licenses = ArrayUtils.ensureArray(keycloakProfile?.attributes?.licentie);
    // if (licenses.length === 0 && roles.some((role) => kibRoles.includes(role))) {
    //   const route = window.location.href.split('kib').filter((r) => !!r);
    //   if (route.length === 1) {
    //     this.router.navigateByUrl('kib/inspection-list-list');
    //   }
    // }
  }

  public reload() {
    window.location.reload();
  }

  public goToTask() {
    this.messageService.clear('task-complete');
    this.taskService.taskSidebarVisible$.next(true);
  }

  public closeErrorMessages() {
    this.messageService.clear(this.ERROR_KEY);
  }

  public get ERROR_KEY(): string {
    return ToastUtils.ERROR_KEY;
  }

  public loadWikiPages() {
    this.wikiService.loadAll();
  }

  public addRouteDetector() {
    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((event: NavigationEnd) => {
      this.wikiService.urlChanged(event.urlAfterRedirects);
    });
  }

  private updateHtmlLang(lang: string) {
    document.documentElement.lang = lang;
  }
}
